import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';

import Icons from '../../themes/icons';

export interface Option {
  label: string;
  value: string | number;
  disabled?: boolean;
}

export interface CheckboxOption extends Option {
  percentage?: string;
  percentageColor?: string;
  hideCheckbox?: boolean;
}

interface Props {
  options: CheckboxOption[];
  value: CheckboxOption | CheckboxOption[] | null;
  onChange: (value: CheckboxOption | CheckboxOption[] | null) => void;
  placeholder?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  openUpward?: boolean;
  multiSelectDisplayText?: (count: number) => string;
}

export const CheckboxMultiSelect: React.FC<Props> = ({
  options,
  value,
  onChange,
  placeholder = 'Select options',
  isSearchable = false,
  isClearable = false,
  openUpward = false,
  multiSelectDisplayText,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<HTMLDivElement>(null);

  const selectedOptions = Array.isArray(value) ? value : value ? [value] : [];

  const getDisplayText = () => {
    if (selectedOptions.length === 0) return placeholder;
    if (selectedOptions.length === 1) return selectedOptions[0].label;
    if (multiSelectDisplayText) return multiSelectDisplayText(selectedOptions.length);

    const sortedOptions = [...selectedOptions].sort((a, b) => {
      const getStartValue = (opt: Option) => {
        const str = opt.value.toString();
        if (str === 'All') return -1;
        if (str.endsWith('+')) return parseInt(str.replace('+', ''));
        if (str.includes('-')) return parseInt(str.split('-')[0]);
        return parseInt(str);
      };

      return getStartValue(a) - getStartValue(b);
    });

    const first = sortedOptions[0];
    const last = sortedOptions[sortedOptions.length - 1];

    if (first.value === 'All') return 'All';

    const firstValue = first.value.toString();
    const lastValue = last.value.toString();

    if (firstValue === lastValue) return firstValue;

    let startNum = firstValue;
    if (firstValue.includes('-')) startNum = firstValue.split('-')[0];

    let endNum = lastValue;
    if (lastValue.endsWith('+')) {
      endNum = lastValue;
    } else if (lastValue.includes('-')) {
      endNum = lastValue.split('-')[1];
    }

    return `${startNum}-${endNum}`;
  };

  const toggleOption = (option: CheckboxOption) => {
    const isSelected = selectedOptions.some((sel) => sel.value === option.value);

    if (isSelected) {
      const newSelected = selectedOptions.filter((sel) => sel.value !== option.value);
      onChange(newSelected.length > 0 ? newSelected : null);
    } else {
      if (option.hideCheckbox) {
        onChange([option]);
      } else {
        const filteredSelected = selectedOptions.filter((sel) => !sel.hideCheckbox);
        onChange([...filteredSelected, option]);
      }
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <Container ref={dropdownRef}>
      <Control ref={controlRef} onClick={() => setIsOpen(!isOpen)} isOpen={isOpen}>
        <DisplayText>{getDisplayText()}</DisplayText>
        <ArrowIcon isOpen={isOpen}>
          <Icons.ExpandMoreIcon />
        </ArrowIcon>
      </Control>

      {isOpen && (
        <DropdownMenu openUpward={openUpward}>
          {options.map((option, index) => {
            const isSelected = selectedOptions.some((sel) => sel.value === option.value);
            const isLastItem = index === options.length - 1;

            return (
              <OptionItem
                key={option.value}
                onClick={() => toggleOption(option)}
                isSelected={isSelected}
                isLastItem={isLastItem}
              >
                {!option.hideCheckbox && (
                  <CheckboxContainer>
                    <Checkbox checked={isSelected}>
                      {isSelected && <CheckIcon>✓</CheckIcon>}
                    </Checkbox>
                  </CheckboxContainer>
                )}
                <OptionContent hideCheckbox={option.hideCheckbox}>
                  <OptionLabel>{option.label}</OptionLabel>
                  {option.percentage && (
                    <PercentageText color={option.percentageColor}>
                      {option.percentage}
                    </PercentageText>
                  )}
                </OptionContent>
              </OptionItem>
            );
          })}
        </DropdownMenu>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const Control = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.6px 12px;
  border: 1px solid #EFEEEE;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  min-height: 40px;
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const DisplayText = styled.span`
  font-family: "Poppins", "Helvetica", "Arial", sans-serif;
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.75;
  text-transform: none;
  color: #000;
  flex: 1;
`;

const ArrowIcon = styled.div<{ isOpen: boolean }>`
  transition: transform 0.2s ease;
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  display: flex;
  align-items: center;

  svg {
    width: 16px;
    height: 16px;
    color: #666;
  }
`;

const DropdownMenu = styled.div<{ openUpward: boolean }>`
  position: absolute;
  top: ${(props) => (props.openUpward ? 'auto' : '100%')};
  bottom: ${(props) => (props.openUpward ? '100%' : 'auto')};
  left: 0;
  right: 0;
  background: white;
  border: 1px solid rgba(230, 230, 230, 1);
  border-top: ${(props) => (props.openUpward ? '1px solid rgba(230, 230, 230, 1)' : 'none')};
  border-bottom: ${(props) => (props.openUpward ? 'none' : '1px solid rgba(230, 230, 230, 1)')};
  border-radius: ${(props) => (props.openUpward ? '4px 4px 0 0' : '0 0 4px 4px')};
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  box-shadow: 0px 0px 40px 0px rgba(0, 0, 0, 0.1);
  padding: 5px;
  margin-top: ${(props) => (props.openUpward ? '0' : '4px')};
  margin-bottom: ${(props) => (props.openUpward ? '4px' : '0')};
`;

const OptionItem = styled.div<{ isSelected: boolean; isLastItem?: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  background-color: ${(props) => (props.isSelected ? '#f5f5f5' : 'transparent')};
  border-bottom: ${(props) => (props.isLastItem ? 'none' : '0.8px solid rgba(0, 0, 0, 0.08)')};

  &:hover {
    background-color: #f8f9fa;
  }
`;

const CheckboxContainer = styled.div`
  margin-right: 15px;
`;

const Checkbox = styled.div<{ checked: boolean }>`
  width: 20px;
  height: 20px;
  border: 1px solid ${(props) => (props.checked ? '#000' : '#32343A')};
  border-radius: 4px;
  background-color: ${(props) => (props.checked ? '#000' : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const CheckIcon = styled.span`
  color: white;
  font-size: 10px;
  font-weight: bold;
  line-height: 1;
`;

const OptionContent = styled.div<{ hideCheckbox?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  margin-left: ${(props) => (props.hideCheckbox ? '0' : '0')};
`;

const OptionLabel = styled.span`
  font-size: 14px;
  color: #333;
`;

const PercentageText = styled.span<{ color?: string }>`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.color || '#666'};
  margin-left: auto;
`;

